// src/pages/NotificationManagement/index.tsx
// Màn hình "Danh sách Thông báo" - theo .features/thong-bao/ke_hoach_ui_man_thong_bao.md

import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Input } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import { useBrandingTitle } from '../../hooks';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';

import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from 'slices/notifications/thunk';
import { NotificationItem, NotificationType } from 'slices/notifications/types';

import NotificationFilterTabs, { FilterTabKey, FilterTabCounts } from './components/NotificationFilterTabs';
import NotificationListItem from './components/NotificationListItem';
import NotificationEmptyState from './components/NotificationEmptyState';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('notification_management');

const PAGE_SIZE_STEP = 10;

const matchesTab = (item: NotificationItem, tab: FilterTabKey): boolean => {
  switch (tab) {
    case 'urgent':
      return item.type === NotificationType.Urgent;
    case 'warning':
      return item.type === NotificationType.Warning;
    case 'confirm':
      return item.type === NotificationType.Confirmation || item.type === NotificationType.Request;
    case 'info':
      return item.type === NotificationType.Info;
    case 'all':
    default:
      return true;
  }
};

const NotificationManagement: React.FC = () => {
  useBrandingTitle(st('danh_sach_thong_bao_text'));

  const location = useLocation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { items, unreadCount, loading, error } = useSelector((state: any) => state.Notifications);

  const [activeTab, setActiveTab] = useState<FilterTabKey>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE_STEP);

  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, pageSize: 100, sortBy: 'createdAt', sortOrder: 'desc' }));
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Reset "Xem thêm" mỗi khi đổi tab hoặc từ khoá tìm kiếm
  useEffect(() => {
    setVisibleCount(PAGE_SIZE_STEP);
  }, [activeTab, searchTerm]);

  const filteredItems = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return items.filter((item: NotificationItem) => {
      if (!matchesTab(item, activeTab)) return false;
      if (keyword && !item.title.toLowerCase().includes(keyword)) return false;
      return true;
    });
  }, [items, activeTab, searchTerm]);

  const tabCounts: FilterTabCounts = useMemo(() => ({
    all: items.length,
    urgent: items.filter((i: NotificationItem) => i.type === NotificationType.Urgent).length,
    warning: items.filter((i: NotificationItem) => i.type === NotificationType.Warning).length,
    confirm: items.filter((i: NotificationItem) => i.type === NotificationType.Confirmation || i.type === NotificationType.Request).length,
    info: items.filter((i: NotificationItem) => i.type === NotificationType.Info).length,
  }), [items]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;

  const handleItemClick = (item: NotificationItem) => {
    if (!item.isRead) {
      dispatch(markNotificationRead(item.id));
    }
    if (item.link) {
      navigate(item.link);
    }
  };

  const handleMarkAllRead = () => {
    if (unreadCount > 0) {
      dispatch(markAllNotificationsRead());
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('danh_sach_thong_bao_text')} pageTitle="Pages" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Row className="align-items-center gy-2">
                    <Col>
                      <div className="d-flex align-items-center gap-2">
                        <h5 className="mb-0">{st('danh_sach_thong_bao_text')}</h5>
                        {unreadCount > 0 && (
                          <span className="badge bg-danger-subtle text-danger rounded-pill">
                            {unreadCount} {st('chua_doc_text_3')}
                          </span>
                        )}
                      </div>
                    </Col>
                    <Col xs="auto">
                      {PageActionPermission.canDisplay(location.pathname, 'button-mark-all-read') && (
                        <button
                          type="button"
                          className="btn btn-soft-primary btn-sm"
                          data-action-code="button-mark-all-read"
                          data-action-name="Đánh dấu đọc tất cả"
                          disabled={unreadCount === 0}
                          onClick={handleMarkAllRead}
                        >
                          <i className="ri-checkbox-multiple-line align-middle me-1"></i>
                          {st('danh_dau_doc_tat_ca_button')}
                        </button>
                      )}
                    </Col>
                  </Row>

                  <Row className="align-items-center gy-2 mt-2">
                    <Col>
                      <NotificationFilterTabs activeTab={activeTab} onChange={setActiveTab} counts={tabCounts} />
                    </Col>
                    <Col md={3}>
                      <Input
                        type="text"
                        placeholder={st('tim_theo_tieu_de_placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody className="p-0">
                  {loading && items.length === 0 ? (
                    <div className="p-3">
                      {[...Array(4)].map((_, idx) => (
                        <div className="placeholder-glow mb-4" key={idx}>
                          <span className="placeholder col-3 d-block mb-2"></span>
                          <span className="placeholder col-8 d-block mb-1"></span>
                          <span className="placeholder col-6 d-block"></span>
                        </div>
                      ))}
                    </div>
                  ) : visibleItems.length === 0 ? (
                    <NotificationEmptyState />
                  ) : (
                    <div>
                      {visibleItems.map((item: NotificationItem) => (
                        <NotificationListItem key={item.id} item={item} onClick={() => handleItemClick(item)} />
                      ))}
                    </div>
                  )}

                  {hasMore && (
                    <div className="text-center py-3 border-top">
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={() => setVisibleCount((c) => c + PAGE_SIZE_STEP)}
                      >
                        {st('xem_them_button')} <i className="ri-arrow-down-line align-middle"></i>
                      </button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NotificationManagement;
